import React, { useEffect, useState, useMemo } from 'react'
import ReactTooltip from 'react-tooltip'
import { ArrowsClockwise, CheckCircle, MagnifyingGlass, Info } from '@phosphor-icons/react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import ModalContainer from '../../../../../components/ModalContainer/ModelContainer'
import TextField from '../../../../../components/FormLegacy/FormFieldsV2/TextField'

const YardiSyncModal = ({ onClose, dataInputState, yardiCustomRecords, onSave }) => {
  const { t } = useTranslation(['checklists-create-task', 'common'])
  const [fields, setFields] = useState(null)
  const [searchValue, setSearchValue] = useState('')
  const [otherOccupantCustomRecord, setOtherOccupantCustomRecord] = useState(null)
  const [residentCustomRecord, setResidentCustomRecord] = useState(null)
  const [tab, setTab] = useState('residentCustomRecords')

  useEffect(() => {
    setOtherOccupantCustomRecord(dataInputState.yardiOtherOccupantCustomRecord)
    setResidentCustomRecord(dataInputState.yardiResidentCustomRecord)

    setFields(yardiCustomRecords)
  }, [])

  const onFieldSyncClick = (field) => {
    setFields((prevFields) => {
      return {
        ...prevFields,
        [tab]: prevFields[tab].map((prevField) => {
          // sync selected field
          if (prevField.id === field.id) return { ...prevField, synced: !prevField.synced }

          // to unsync previously synced field
          if (
            (otherOccupantCustomRecord &&
              prevField.id === otherOccupantCustomRecord.id &&
              field.id !== otherOccupantCustomRecord.id) ||
            (residentCustomRecord &&
              prevField.id === residentCustomRecord.id &&
              field.id !== residentCustomRecord.id)
          ) {
            return { ...prevField, synced: false }
          }

          return prevField
        }),
      }
    })
    if (tab === 'residentCustomRecords') {
      setResidentCustomRecord(field.synced ? null : field)
    }
    if (tab === 'otherOccupantCustomRecords') {
      setOtherOccupantCustomRecord(field.synced ? null : field)
    }
  }

  const filteredFields = useMemo(() => {
    if (!fields) return []
    if (!searchValue) return fields[tab]
    return fields[tab].filter((field) =>
      field.name.toLowerCase().includes(searchValue.toLowerCase()),
    )
  }, [searchValue, tab, fields])

  return (
    <ModalContainer
      additionalClass="yardi-sync-modal"
      headerTitle={
        <div className="yardi-sync-modal__title">
          {t('checklists-create-task:yardiSyncModal.title')}
          <div className="yardi-sync-modal__title__icon" data-tip data-for="title-tip">
            <Info size={20} />
            <ReactTooltip
              className="yardi-sync-modal__title__tooltip"
              multiline={true}
              id="title-tip"
              place="top"
              effect="solid"
            >
              {t('checklists-create-task:yardiSyncModal.titleTooltip')}
            </ReactTooltip>
          </div>
        </div>
      }
      headerIcon={<ArrowsClockwise size={20} />}
      onClose={onClose}
    >
      <div className="yardi-sync-modal__tabs-and-search-container">
        <div className="yardi-sync-modal__tabs">
          <div
            className={`yardi-sync-modal__tab${
              tab === 'residentCustomRecords' ? ' yardi-sync-modal__tab__active' : ''
            }`}
            onClick={() => setTab('residentCustomRecords')}
          >
            {t('checklists-create-task:yardiSyncModal.residentsTab')}
          </div>
          <div
            className={`yardi-sync-modal__tab${
              tab === 'otherOccupantCustomRecords' ? ' yardi-sync-modal__tab__active' : ''
            }`}
            onClick={() => setTab('otherOccupantCustomRecords')}
          >
            {t('checklists-create-task:yardiSyncModal.otherOccupantsTab')}
          </div>
        </div>
        <TextField
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder={t('checklists-create-task:yardiSyncModal.searchPlaceholder')}
          leftIcon={<MagnifyingGlass size={20} />}
        />
      </div>
      <div className="yardi-sync-modal__fields-container">
        {filteredFields.map((field) => {
          const isSynced =
            field.synced ||
            (residentCustomRecord && field.id === residentCustomRecord.id) ||
            (otherOccupantCustomRecord && field.id === otherOccupantCustomRecord.id)
          const isDisabled =
            field.synced &&
            ((tab === 'residentCustomRecords' &&
              residentCustomRecord &&
              field.id !== residentCustomRecord.id) ||
              (tab === 'otherOccupantCustomRecords' &&
                otherOccupantCustomRecord &&
                field.id !== otherOccupantCustomRecord.id) ||
              (tab === 'residentCustomRecords' && !residentCustomRecord) ||
              (tab === 'otherOccupantCustomRecords' && !otherOccupantCustomRecord))

          return (
            <div className="yardi-sync-modal__field" key={field.id}>
              <div>{field.name}</div>
              <button
                data-tip={isDisabled}
                data-for={isDisabled ? 'disabled-tip' : undefined}
                disabled={isDisabled}
                className={`yardi-sync-modal__field__button${
                  isSynced ? ' yardi-sync-modal__field__button__synced' : ''
                }`}
                onClick={() => onFieldSyncClick(field)}
              >
                {isSynced ? (
                  <React.Fragment>
                    <CheckCircle size={15} /> {t('checklists-create-task:yardiSyncModal.synced')}
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <ArrowsClockwise size={15} /> {t('checklists-create-task:yardiSyncModal.sync')}
                  </React.Fragment>
                )}
                {isDisabled && (
                  <ReactTooltip
                    className="yardi-sync-modal__field__button__tooltip"
                    id="disabled-tip"
                    multiline={true}
                    place="top"
                    effect="solid"
                  >
                    {t('checklists-create-task:yardiSyncModal.fieldSyncedElsewhereTooltip')}
                  </ReactTooltip>
                )}
              </button>
            </div>
          )
        })}
      </div>
      <div className="yardi-sync-modal__footer">
        <button onClick={onClose} className="button button-gray">
          {t('common:actions.cancel')}
        </button>
        <button
          onClick={() =>
            onSave({
              residentCustomRecord,
              otherOccupantCustomRecord,
              fields,
            })
          }
          className="button button-primary"
        >
          {t('common:actions.save')}
        </button>
      </div>
    </ModalContainer>
  )
}

YardiSyncModal.propTypes = {
  onClose: PropTypes.func,
  selectedBuilding: PropTypes.object,
  dataInputState: PropTypes.object,
  yardiCustomRecords: PropTypes.object,
  setYardiCustomRecords: PropTypes.func,
  onSave: PropTypes.func,
}

export default YardiSyncModal
